import { Link } from 'found';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import { ButtonGroup, ButtonToolbar } from 'reactstrap';

import Loading from 'src/components/Loading';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import LinkInI18nBlock from 'src/enosikit/components/LinkInI18nBlock';
import getFullAddressString from 'src/util/address';
import { allTariffs } from 'src/util/meterTariff';

import TariffInfo from '../TariffInfo';

const TariffListFragment = graphql`
  fragment TariffList_property on Property @throwOnFieldError {
    uuid
    title
    address {
      line1
      line2
      city
      state
      postcode
    }
    meters(first: 500) {
      edges {
        node {
          uuid
          identifier
          title
          tariffs(first: 500) {
            edges {
              node {
                uuid
                identifier
                tariffType
                title
                active { start finish }
                ...TariffInfo_tariff
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * TariffList functional component displays all tariffs for a property, grouped by meter.
 * @param {object} props - Component props.
 * @param {object} props.property - Property fragment reference.
 * @returns {React.ReactElement} TariffList component.
 */
function TariffList({ property = null }) {
  const intl = useIntl();
  const propertyData = useFragment(TariffListFragment, property);

  if (!propertyData) {
    return <Loading />;
  }

  const {
    uuid, address, title, meters,
  } = propertyData;

  const addr = getFullAddressString(address);

  const pageTitle = intl.formatMessage({ id: 'tariff.tariff_list.page.title', defaultMessage: 'Enosi - Property: Tariffs' });
  const breadcrumbProperties = intl.formatMessage({ id: 'tariff.tariff_list.properties.breadcrumb', defaultMessage: 'Properties' });
  const breadcrumbTariffs = intl.formatMessage({ id: 'tariff.tariff_list.tariffs.breadcrumb', defaultMessage: 'Tariffs' });
  const headingTariffs = intl.formatMessage({ id: 'tariff.tariff_list.tariffs.heading', defaultMessage: 'Tariffs' });
  const propertySettingsLabel = intl.formatMessage({ id: 'tariff.tariff_list.property_settings.label', defaultMessage: 'Property Settings' });

  const propertyControls = (
    <ButtonToolbar>
      <ButtonGroup className="ms-2">
        <Link to={`/properties/${uuid}/settings`} className="btn btn-darken">
          {propertySettingsLabel}
        </Link>
      </ButtonGroup>
    </ButtonToolbar>
  );

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbProperties, path: '/properties' },
          { name: title, path: `/properties/${uuid}` },
          { name: breadcrumbTariffs },
        ]}
      />
      <Heading
        title={title}
        subtitle={addr}
        context={headingTariffs}
        controls={propertyControls}
      />
      <p>
        <FormattedMessage
          id="tariff.tariff_list.tariffs.help_text"
          defaultMessage="Your electricity tariffs are listed here and are GST exclusive."
          values={{
            link: (chunks) => (
              <LinkInI18nBlock
                url={intl.formatMessage({ id: 'tariff.tariff_list.tariffs.help_text.url', defaultMessage: 'tariff.tariff_list.tariffs.help_text.url' })}
                urlKey="tariff.tariff_list.tariffs.help_text.url"
                chunks={chunks}
              />
            ),
          }}
        />
      </p>
      <div className="property-all-tariffs-list">
        {meters.edges.map((el) => el.node).map((meter) => (
          <React.Fragment key={meter.uuid}>
            <hr />
            <div>
              <h3>
                {meter.title}
                <br />
                <small>{meter.identifier}</small>
              </h3>

              {meter.tariffs && meter.tariffs.edges
                && allTariffs(meter.tariffs.edges).map((tariff) => (
                  <TariffInfo tariff={tariff} key={tariff.uuid} />
                ))}
            </div>
          </React.Fragment>
        ))}
      </div>
    </>
  );
}

TariffList.propTypes = {
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default TariffList;
